import { Component } from '@theme/component';

/**
 * Announcement banner custom element that slides vertically between content.
 *
 * @extends {Component}
 */
export class AnnouncementBar extends Component {
  #currentIndex = 0;
  #intervalId = undefined;
  #slides = [];
  #slidesContainer = null;
  #slideHeight = 44; // px, matches CSS

  connectedCallback() {
    super.connectedCallback();

    // Wait for DOM to be ready
    requestAnimationFrame(() => {
      this.#init();
    });

    this.addEventListener('mouseenter', () => this.#clearInterval());
    this.addEventListener('mouseleave', () => this.#startInterval());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.#clearInterval();
      } else {
        this.#startInterval();
      }
    });
  }

  disconnectedCallback() {
    this.#clearInterval();
  }

  #init() {
    this.#slidesContainer = this.querySelector('.announcement-bar__slides');
    this.#slides = Array.from(this.querySelectorAll('.announcement-bar__slide'));

    if (!this.#slidesContainer || this.#slides.length <= 1) return;

    // Get the interval from the autoplay attribute (in seconds)
    const autoplayAttr = this.getAttribute('autoplay');
    const intervalSeconds = parseInt(autoplayAttr, 10);
    if (isNaN(intervalSeconds) || intervalSeconds <= 0) return;

    this.#startInterval(intervalSeconds * 1000);
  }

  #startInterval(ms) {
    this.#clearInterval();
    // Use the stored value if ms not provided
    const delay = ms ?? this.#getIntervalMs();
    if (!delay) return;
    this.#intervalId = setInterval(() => {
      if (document.hidden) return;
      this.#goToNext();
    }, delay);
  }

  #clearInterval() {
    if (this.#intervalId !== undefined) {
      clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }
  }

  #getIntervalMs() {
    const autoplayAttr = this.getAttribute('autoplay');
    const seconds = parseInt(autoplayAttr, 10);
    return isNaN(seconds) ? null : seconds * 1000;
  }

  #goToNext() {
    this.#currentIndex = (this.#currentIndex + 1) % this.#slides.length;
    this.#updatePosition();
  }

  #updatePosition() {
    if (!this.#slidesContainer) return;
    const offset = this.#currentIndex * -this.#slideHeight;
    this.#slidesContainer.style.transform = `translateY(${offset}px)`;

    this.#slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== this.#currentIndex}`);
    });
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
